import React, {useEffect} from 'react'

import {Button, NakedButton} from '../../../components/Button'
import {Container, ColContainer, TPL_50_50, CardContainer} from '../../../components/Container';
import {GraphqlErrorMessage, ValidationErrorMessage} from '../../../components/ErrorMessage';
import {H1, H2} from '../../../components/Typography';
import {Helmet} from 'react-helmet';
import {Input} from '../../../components/Input';
import {SelectInput} from '../../../components/SelectInput';
import {useFormState} from 'react-use-form-state'
import {useMutation, useQuery} from "@apollo/client"
import {withInstallation} from '../../../components/InstallationContext';
import {Loading, LoadingError} from '../../../components/LoadingFork';

import {
    createUserMutation,
    rolesQuery,
} from './queries';
import {hideGlobalLoader, showGlobalLoader} from "../../../lib/loaderController";

function UserCreate({installation, history, match}) {
    const {
        data: rolesData = {},
        loading: rolesLoading,
        error: rolesError,
    } = useQuery(rolesQuery, {
        variables: {
            installationId: installation.id,
        },
        fetchPolicy: 'no-cache',
    });

    const roles = rolesData.roles || [];
    const roleOptions = roles.map((role) => ({
        value: role.id,
        label: role.label || role.name,
    }));

    const [{errors, values, setField}, {text, email, tel, select, password}] = useFormState({
        name: '',
        email: '',
        phone: '',
        roleId: '',
    })

    useEffect(() => {
        if (values.roleId || roles.length === 0) return;

        const receptionistRoles = roles.filter(
            (role) => role.name === 'hotel:receptionist'
        );
        const receptionistRole =
            receptionistRoles.find((role) => !role.installationId) ||
            receptionistRoles[0];

        if (receptionistRole) {
            setField('roleId', receptionistRole.id);
        }
    }, [roles, setField, values.roleId]);

    const [createUser, {error: createUserError}] = useMutation(createUserMutation);
    const submitError = createUserError;
    const firstError = Object.values(errors)[0]
    const hasErrors = !!firstError
    const canSubmit = (
      !hasErrors &&
      values.name &&
      values.email &&
      values.roleId &&
      values.password &&
      values.password === values.passwordConfirm
    )

    async function onCreate() {
        try {
            showGlobalLoader();
            const {data: { createUser: { id: userId } }} = await createUser({
                variables: {
                    name: values.name,
                    email: values.email,
                    phone: values.phone,
                    password: values.password,
                    roleId: values.roleId,
                },
            })

            history.push(match.url.replace('/new', `/${userId}`), {
                flash: {
                    flavour: 'success',
                    message: `A new account for ${values.name} has been created!`,
                    createdAt: Date.now(),
                },
            })
        } catch (e) {
            console.error(e);
            history.push(match.url.replace('/new', `/`), {
                flash: {
                    flavour: 'error',
                    message: 'Cannot create user. Please try again.',
                    createdAt: Date.now(),
                },
            })
        } finally {
            hideGlobalLoader();
        }
    }

    function onCancel() {
        history.goBack()
    }

    function validatePassword(value, state) {
        if (state.password === state.passwordConfirm) return
        return 'Passwords do not match'
    }

    if (rolesLoading) {
        return (
          <CardContainer>
              <Loading />
          </CardContainer>
        );
    }

    if (rolesError) {
        return (
          <CardContainer>
              <LoadingError error={rolesError} />
          </CardContainer>
        );
    }

    return (
      <div>
          <CardContainer>
              <Container spaceAbove="medium" spaceBelow="medium">
                  <Helmet>
                      <title>New user</title>
                  </Helmet>
                  <H1>New user</H1>
                  {submitError && <GraphqlErrorMessage spaceBelow="medium" error={submitError}/>}
                  {hasErrors && <ValidationErrorMessage spaceBelow="medium" error={firstError}/>}

                  <ColContainer columnTemplate={TPL_50_50} spaceAbove="medium">
                      <Input required
                             label="Name"
                             error={errors.name}
                             {...text('name')} />
                      <SelectInput
                        required
                        label="Role"
                        error={errors.roleId}
                        options={roleOptions}
                        {...select('roleId')}
                      />
                  </ColContainer>
                  <ColContainer columnTemplate={TPL_50_50} spaceBelow="medium">
                      <Input required
                             label="Email address"
                             error={errors.email}
                             {...email({
                                 name: 'email',
                                 validateOnBlur: true
                             })} />
                      <Input label="Phone number" {...tel('phone')} />
                  </ColContainer>

                  <H2>Password</H2>
                  <ColContainer columnTemplate={TPL_50_50}>
                      <Input required
                             label="Password"
                             placeholder="Password"
                             minLength={8}
                             error={errors.password}
                             {...password('password')} />
                      <Input required
                             label="Confirm Password"
                             placeholder="Confirm Password"
                             error={errors.passwordConfirm}
                             {...password({
                                 name: 'passwordConfirm',
                                 validate: validatePassword,
                                 validateOnBlur: true
                             })} />
                  </ColContainer>
                  <div>
                      <Button type="submit" style={{'cursor':'pointer'}} spaceAbove="small" disabled={!canSubmit} onClick={onCreate}>
                          Create User
                      </Button>
                      <NakedButton style={{'cursor':'pointer'}} flavour="gray" spaceAbove="small" onClick={onCancel}>
                          Cancel
                      </NakedButton>
                  </div>
              </Container>
          </CardContainer>
      </div>
    );
}

export default withInstallation(UserCreate)